import { useState, useEffect, useCallback, useRef } from 'react';
import { notificationsAPI } from '../services/api';

const POLL_INTERVAL_MS = 60_000; // 60 s — polite background polling

/**
 * useNotifications — fetches and manages in-app notifications for the
 * active facility.
 *
 * @param {Object} [options]
 * @param {boolean} [options.poll=false]   Enable background polling every 60 s.
 * @param {boolean} [options.unreadOnly]   Fetch unread notifications only.
 * @param {number}  [options.limit=50]     Page size.
 *
 * @returns {{
 *   notifications: Array,
 *   unreadCount: number,
 *   loading: boolean,
 *   error: string,
 *   refresh: Function,
 *   markRead: Function,
 *   markAllRead: Function,
 * }}
 */
export function useNotifications({ poll = false, unreadOnly = false, limit = 50 } = {}) {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const intervalRef = useRef(null);

  const load = useCallback(async () => {
    try {
      setError('');
      const token = localStorage.getItem('auth_token');
      if (!token) {
        setNotifications([]);
        setUnreadCount(0);
        return;
      }

      const params = {};
      if (unreadOnly) params.unread = 'true';
      if (limit !== 50) params.limit = limit;

      const res = await notificationsAPI.list(params);
      if (!res.success) throw new Error(res.message || 'Failed to load notifications');

      setNotifications(res.data || []);
      setUnreadCount(res.meta?.unread ?? 0);
    } catch (e) {
      // Graceful degradation — show empty state rather than crash
      setError(e.message || 'Failed to load notifications');
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, [unreadOnly, limit]);

  const markRead = useCallback(async (id) => {
    try {
      await notificationsAPI.markRead(id);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, is_read: true, read_at: new Date().toISOString() } : n))
      );
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch (e) {
      console.error('markRead failed:', e);
    }
  }, []);

  const markAllRead = useCallback(async () => {
    try {
      await notificationsAPI.markAllRead();
      setNotifications((prev) =>
        prev.map((n) => ({ ...n, is_read: true, read_at: n.read_at || new Date().toISOString() }))
      );
      setUnreadCount(0);
    } catch (e) {
      console.error('markAllRead failed:', e);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!poll) return undefined;
    intervalRef.current = setInterval(load, POLL_INTERVAL_MS);
    return () => clearInterval(intervalRef.current);
  }, [poll, load]);

  return {
    notifications,
    unreadCount,
    loading,
    error,
    refresh: load,
    markRead,
    markAllRead,
  };
}

export default useNotifications;
